import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { SOAListTable } from './SOAListTable';
import { getPropertyList } from '../../../Inventory/utils/api';
import { PropertySelector } from './PropertySelector';
import { getSOAList, deleteSOA } from '../api';

const SOAPreviewWrapper = () => {
    const dispatch = useDispatch();
    const [properties, setProperties] = useState([]);
    const [soas, setSOAs] = useState([]);
    const [user, setUser] = useState(null);
    const [selectedProperty, setSelectedProperty] = useState({
        property_id: '',
        building_name: '',
        address: '',
        postal_code: '',
    });
    const [filters, setFilters] = useState({
        name: '',
        unitNo: '',
        dateRangeStart: null,
        dateRangeEnd: null,
        status: 'pending',
    });

    const getUserFromToken = useCallback(() => {
        try {
            const token = localStorage.getItem('accessToken');
            return token ? jwt_decode(token) : null;
        } catch {
            return null;
        }
    }, []);

    const fetchProperties = useCallback(async () => {
        const user = getUserFromToken();
        if (!user) return;

        setUser(user);

        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const result = await getPropertyList(user?.role || '', user?.id || '');
            setProperties(result?.propertyList || []);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Oops...',
                html: '<p class="text-lg">We couldn’t fetch the properties. Please try again later!</p>',
                confirmButtonColor: '#3085d6',
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [dispatch, getUserFromToken]);

    const fetchSOAs = useCallback(async (propertyId, buildingName, filters) => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const { data } = await getSOAList({
                propertyId: propertyId || null,
                buildingName: buildingName || null,
                name: filters.name,
                unitNo: filters.unitNo,
                dateRangeStart: filters.dateRangeStart,
                dateRangeEnd: filters.dateRangeEnd,
                status: filters.status,
            });
            setSOAs(data || []);
        } catch (error) {
            console.error('[fetchSOAs] Error:', error);
            setSOAs([]);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [dispatch]);

    const handleDelete = useCallback(async (soa) => {
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: `Do you want to delete the SOA for ${soa.tenant?.[0]?.name || 'this tenant'}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#f46a6a',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, delete it!',
            });

            if (result.isConfirmed) {
                dispatch({ type: 'SET_LOADING', payload: true });
                await deleteSOA(soa._id);
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted',
                    text: 'Statement of Account deleted successfully!',
                    confirmButtonColor: '#34c38f',
                });
                fetchSOAs(selectedProperty.property_id, selectedProperty.building_name, filters);
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Failed to delete SOA. Please try again!',
                confirmButtonColor: '#f46a6a',
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [dispatch, fetchSOAs, selectedProperty.property_id, selectedProperty.building_name, filters]);

    const handlePropertySelectionChange = useCallback((newValues) => {
        setSelectedProperty(newValues);
    }, []);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    const handleSuccess = useCallback(() => {
        fetchSOAs(selectedProperty.property_id, selectedProperty.building_name, filters);
    }, [fetchSOAs, selectedProperty.property_id, selectedProperty.building_name, filters]);

    useEffect(() => {
        fetchProperties();
    }, [fetchProperties]);

    useEffect(() => {
        fetchSOAs(selectedProperty.property_id, selectedProperty.building_name, filters);
    }, [selectedProperty.property_id, selectedProperty.building_name, filters, fetchSOAs]);

    return (
        <div className="page-content">
            <MetaTags>
                <title>Rentdigicare | SOA List</title>
            </MetaTags>
            <div className="container-fluid">
                <Breadcrumbs
                    title="Property Details"
                    breadcrumbItem="SOA List"
                />
                <PropertySelector
                    propertyList={properties}
                    selectedProperty={selectedProperty}
                    onPropertySelectionChange={handlePropertySelectionChange}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    isLoading={false}
                    user={user}
                    hideAddButton={true}
                />
                <SOAListTable
                    items={soas}
                    onDelete={handleDelete}
                    onRefresh={handleSuccess}
                />
            </div>
        </div>
    );
};

export { SOAPreviewWrapper };